import * as React from 'react';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Box, styled, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { IEmployee, IEquipment } from 'src/@types/apiResponseTypes';
import { equipmentApi } from 'src/api';
import LogoImageSrc from 'src/assets/images/innerwave_logo.png';
import LogoWhiteImageSrc from 'src/assets/images/innerwave_logo_white.png';
import CDialog from 'src/components/CDialog';
import { CustomAvatar } from 'src/components/custom-avatar';
import ProfileViewModal, { ProfileViewModalProps } from 'src/components/ProfileViewModal';
import getProfileImg from 'src/utils/getProfileImg';
/**
 * ## EmpDetailDialog 설명
 */
export interface SimpleDialogProps {
  open: boolean;
  selectedValue?: IEmployee;
  onClose: () => void;
}
export const EmpDetailDialog = observer((props: SimpleDialogProps) => {
  const { onClose, selectedValue, open } = props;
  const theme = useTheme();
  const logoScr = theme.palette.mode === 'light' ? LogoImageSrc : LogoWhiteImageSrc;
  const logo = <LogoImage src={logoScr} alt="logo" />;
  const [profileViewModalInfo, setProfileViewModalInfo] = React.useState<ProfileViewModalProps>({
    open: false,
    profileUrl: getProfileImg(selectedValue?.profileImgUrl),
  });

  // 프로필 이미지 클릭 시 크게 보여주는 모달 열기
  const handleClickProfile = () => {
    setProfileViewModalInfo({
      open: true,
      profileUrl: getProfileImg(selectedValue?.profileImgUrl),
      onClose: () => setProfileViewModalInfo({ ...profileViewModalInfo, open: false }),
    });
  };

  const [equipments, setEquipments] = React.useState<IEquipment[]>([]);

  React.useEffect(() => {
    (async () => {
      if (selectedValue) {
        const result = await equipmentApi.gets({
          text: '',
          page: 1,
          size: 1000,
        });
        if (result) {
          setEquipments(
            result.data
              .filter((r) => r.userSid === selectedValue.userSid)
              .sort((a: any, b: any) => {
                return b.inventoryId - a.inventoryId;
              }),
          );
        }
      }
    })();
  }, []);

  return (
    <>
      <CDialog
        handleDialogClose={onClose}
        open={open}
        titleIcon={logo}
        dialogTitle={''}
        initialHeight={530}
        height={'100%'}
        enableScrollbar={false}
        isOverflow={false}
        logofooter={true}
        onClickBackground={onClose}
        dimStyle={true}
      >
        <Container>
          <CustomAvatar
            src={getProfileImg(selectedValue?.profileImgUrl)}
            alt={selectedValue?.empNm}
            name={selectedValue?.empNm}
            sx={{ width: 150, height: 150, marginTop: '15px' }}
            onClick={handleClickProfile}
          />
          <TypoContainer>
            <TypoEmpPosCd>{selectedValue?.empPosCd?.value}</TypoEmpPosCd>
            <TypoName>{selectedValue?.empNm}</TypoName>
            <FlexBox>
              <PhoneIphoneIcon />
              <TypoContent>{selectedValue?.phone ?? 'no phone number'}</TypoContent>
            </FlexBox>
            <FlexBox>
              <EmailIcon />
              <TypoContent>
                <a href={`mailto:${selectedValue?.user?.loginId}`}>
                  {selectedValue?.user?.loginId}
                </a>
              </TypoContent>
            </FlexBox>
          </TypoContainer>
        </Container>
      </CDialog>
      <ProfileViewModal {...profileViewModalInfo} />
    </>
  );
});

export default EmpDetailDialog;

const LogoImage = styled('img')(({ theme }) => ({
  width: 140,
  height: 'auto',
  marginTop: '13px',
  marginBottom: '13px',
}));
const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  alignItems: 'center',
  margin: '20px',
}));

const TypoContainer = styled(Box)(({ theme }) => ({
  margin: '20px',
  paddingTop: '30px',
  paddingBottom: '30px',
  justifyContent: 'center',
  textAlign: 'center',
}));

const TypoName = styled(Box)(({ theme }) => ({
  padding: '3px',
  fontSize: theme.typography.h2.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  position: 'relative',
}));

const TypoEmpPosCd = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
  color: theme.palette.grey[600],
}));

const TypoContent = styled(Box)(({ theme }) => ({
  padding: '5px',
  fontSize: theme.typography.h4.fontSize,
  color: theme.palette.grey[600],
}));

const FlexBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
}));
